import { datadogLogs } from "@datadog/browser-logs";
import { PARTNER_988, type Partner } from "app/redux/slices/authSlice";

export type Program = "P988" | "CTL_WEBCHAT";

/**
 * Maps the texter's partner to the messaging-metrics `program` tag. Mirrors the
 * platform numerator (CAT-2537): 988 → P988, all other webchat (CTL website,
 * Google Classroom) → CTL_WEBCHAT. Both ends must agree so the delivery rate
 * splits cleanly by program.
 */
export function programForPartner(partner: Partner): Program {
  return partner === PARTNER_988 ? "P988" : "CTL_WEBCHAT";
}

const RECEIVED_EMITTED_KEY = "webchatReceivedEmittedIds";
const MAX_TRACKED_IDS = 200;

function readEmittedIds(): string[] {
  try {
    const raw = sessionStorage.getItem(RECEIVED_EMITTED_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

/**
 * Replay-guard for the inbound `received` denominator. Records the client
 * messageId (= remote_message_id) in sessionStorage and returns true only the
 * first time a given id is seen, so a page refresh / socket reconnect that
 * replays a send does not double-count it.
 *
 * Fails open if sessionStorage is unavailable (e.g. private browsing): a rare
 * double-count is preferable to dropping the denominator entirely. The tracked
 * set is bounded — a single session never approaches the limit.
 */
export function markReceivedEmittedOnce(messageId: string): boolean {
  try {
    const ids = readEmittedIds();
    if (ids.includes(messageId)) {
      return false;
    }
    ids.push(messageId);
    sessionStorage.setItem(
      RECEIVED_EMITTED_KEY,
      JSON.stringify(ids.slice(-MAX_TRACKED_IDS))
    );
    return true;
  } catch {
    return true;
  }
}

/**
 * The inbound delivery-rate denominator. Every texter message must produce
 * exactly one of these, including the conversation-opening keyword — which has
 * no client messageId, so remote_message_id is omitted for it (matching the
 * platform numerator, which omits the tag for that message too).
 */
export function emitInboundReceived(
  partner: Partner,
  remoteMessageId?: string
): void {
  datadogLogs.logger.info("Webchat message received from provider", {
    metric: "messaging.inbound_provider_message.received",
    message_provider: "webchat",
    modality: "webchat",
    program: programForPartner(partner),
    ...(remoteMessageId !== undefined
      ? { remote_message_id: remoteMessageId }
      : {}),
  });
}
